import type {
  EquipmentBand,
  EquipmentItem,
  EquipmentScore,
  EquipmentTier,
  HullGroup,
} from './api-client';

/**
 * Presentation helpers for the equipment index. The index itself is computed on the
 * API — see apps/api/src/equipment/equipment-score.ts and
 * docs/specs/2026-09-01-equipment-score-design.md. 100 = the median boat of the same
 * hull type.
 */

const BAND_LABELS: Record<EquipmentBand, string> = {
  basic: 'Basic',
  typical: 'Typical',
  well_equipped: 'Well equipped',
  premium: 'Premium',
};

export function bandLabel(band: EquipmentBand): string {
  return BAND_LABELS[band];
}

const BAND_TONES: Record<EquipmentBand, string> = {
  basic: 'border-transparent bg-muted text-muted-foreground',
  typical: 'border-transparent bg-sky-500/10 text-sky-700 dark:text-sky-300',
  well_equipped: 'border-transparent bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  premium: 'border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

export function bandToneClass(band: EquipmentBand): string {
  return BAND_TONES[band];
}

/**
 * The signed gap against the reference boat, or null when either side is unscored.
 * Uses U+2212 MINUS SIGN — a hyphen-minus renders noticeably lighter than the
 * digits beside it in the table.
 */
export function formatScoreDelta(
  subject: EquipmentScore | null,
  reference: EquipmentScore | null
): string | null {
  if (!subject || !reference) return null;
  const delta = subject.score - reference.score;
  if (delta === 0) return 'level';
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
}

/** "3 pts" / "1 pt", or an em-dash for an item that carries no points at all. */
export function formatPoints(points: number | null): string {
  if (points === null) return '—';
  return `${points} ${points === 1 ? 'pt' : 'pts'}`;
}

/** Total points of a list, skipping items MMK added after the weight table. */
export function sumPoints(items: EquipmentItem[]): number {
  return items.reduce((total, item) => total + (item.points ?? 0), 0);
}

export interface EquipmentDiff {
  /** On the competitor, missing from the reference boat — the upsell. */
  theyHave: EquipmentItem[];
  /** On the reference boat, missing from the competitor — the justification. */
  youHave: EquipmentItem[];
  shared: EquipmentItem[];
}

const TIER_ORDER: Record<string, number> = { premium: 0, comfort: 1, baseline: 2 };

/** Premium first, then comfort, baseline, and anything MMK added since the weights. */
function byTierThenName(a: EquipmentItem, b: EquipmentItem): number {
  const rank = (i: EquipmentItem) => (i.tier ? (TIER_ORDER[i.tier] ?? 3) : 3);
  const diff = rank(a) - rank(b);
  return diff !== 0 ? diff : a.name.localeCompare(b.name);
}

/**
 * Head-to-head equipment comparison against the reference boat. This is the part
 * that does the real work: it explains a score gap with concrete objects, and it
 * makes no numeric claim that can be wrong.
 *
 * Matches on `externalId` (MMK's stable id) rather than `id`, whose serial is
 * re-issued whenever the equipment catalogue resyncs.
 */
export function buildEquipmentDiff(
  referenceEquipment: EquipmentItem[],
  competitorEquipment: EquipmentItem[]
): EquipmentDiff {
  const referenceIds = new Set(referenceEquipment.map((i) => i.externalId));
  const competitorIds = new Set(competitorEquipment.map((i) => i.externalId));

  return {
    theyHave: competitorEquipment.filter((i) => !referenceIds.has(i.externalId)).sort(byTierThenName), // prettier-ignore
    youHave: referenceEquipment
      .filter((i) => !competitorIds.has(i.externalId))
      .sort(byTierThenName),
    shared: competitorEquipment.filter((i) => referenceIds.has(i.externalId)).sort(byTierThenName),
  };
}

export interface EquipmentTierGroup {
  /** null for items MMK added after the weight table — they score nothing. */
  tier: EquipmentTier | null;
  items: EquipmentItem[];
}

const TIER_GROUPS: (EquipmentTier | null)[] = ['premium', 'comfort', 'baseline', null];

/**
 * A boat's equipment grouped for display, most valuable first. Empty groups are
 * dropped so a section never renders with nothing under it.
 */
export function groupEquipmentByTier(items: EquipmentItem[]): EquipmentTierGroup[] {
  return TIER_GROUPS.map((tier) => ({
    tier,
    items: items
      .filter((i) => i.tier === tier)
      .sort((a, b) => (b.points ?? 0) - (a.points ?? 0) || a.name.localeCompare(b.name)),
  })).filter((group) => group.items.length > 0);
}

const HULL_LABELS: Record<HullGroup, string> = {
  sail: 'sailing boat',
  motor: 'motor boat',
};

/** The one-line explanation of where a boat's index came from. */
export function describeScore(score: EquipmentScore): string {
  const hull = HULL_LABELS[score.hullGroup];
  return `${score.points} points from ${score.itemsCounted} of ${score.itemsApplicable} items typical for a ${hull} — the median ${hull} earns ${score.medianPoints}`;
}
